import { apiRequest } from "./apiClient";

export type DeviceLogEntry = {
  id: number;
  requestAt: string;
  deviceId: string;
  cardNumber: string | null;
  requestTypeId: number | null;
  requestTypeName: string | null;
  message: string | null;
  requestObject: unknown;
};

export type DeviceLogsMeta = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

type DeviceLogDto = {
  id: number;
  request_at: string;
  device_id: string;
  card_number: string | null;
  request_type_id: number | null;
  request_type_name: string | null;
  message: string | null;
  request_object: unknown;
};

type DeviceLogResponseDto = {
  items: DeviceLogDto[];
  meta: {
    page: number;
    page_size: number;
    total: number;
    pages: number;
  };
};

export type FetchDeviceLogsParams = {
  page?: number;
  pageSize?: number;
  deviceId?: string;
  cardNumber?: string;
  requestTypeId?: string;
  fromDate?: string;
  toDate?: string;
  signal?: AbortSignal;
};

const buildLogsQuery = (params?: FetchDeviceLogsParams) => {
  if (!params) {
    return "";
  }
  const search = new URLSearchParams();
  if (params.page && params.page > 0) {
    search.append("page", String(params.page));
  }
  if (params.pageSize && params.pageSize > 0) {
    search.append("pageSize", String(params.pageSize));
  }
  if (params.deviceId?.trim()) {
    search.append("deviceId", params.deviceId.trim());
  }
  if (params.cardNumber?.trim()) {
    search.append("cardNumber", params.cardNumber.trim());
  }
  if (params.requestTypeId?.trim()) {
    search.append("requestTypeId", params.requestTypeId.trim());
  }
  if (params.fromDate) {
    search.append("fromDate", params.fromDate);
  }
  if (params.toDate) {
    search.append("toDate", params.toDate);
  }
  const query = search.toString();
  return query ? `?${query}` : "";
};

const mapDeviceLog = (dto: DeviceLogDto): DeviceLogEntry => ({
  id: dto.id,
  requestAt: dto.request_at,
  deviceId: dto.device_id,
  cardNumber: dto.card_number,
  requestTypeId: dto.request_type_id,
  requestTypeName: dto.request_type_name,
  message: dto.message,
  requestObject: dto.request_object,
});

export async function fetchDeviceLogs(params?: FetchDeviceLogsParams) {
  const query = buildLogsQuery(params);
  const response = await apiRequest<DeviceLogResponseDto>(`/api/device-logs${query}`, {
    signal: params?.signal,
  });
  return {
    items: response.items.map(mapDeviceLog),
    meta: {
      page: response.meta.page,
      pageSize: response.meta.page_size,
      total: response.meta.total,
      pages: response.meta.pages,
    } as DeviceLogsMeta,
  };
}

export type DeviceRequestType = {
  id: number;
  name: string;
};

type DeviceRequestTypeDto = {
  id: number;
  request_name?: string | null;
  name?: string | null;
};

export async function fetchDeviceRequestTypes(signal?: AbortSignal) {
  const response = await apiRequest<DeviceRequestTypeDto[]>("/api/device-logs/request-types", {
    signal,
  });
  return response.map((item) => ({
    id: item.id,
    name: item.request_name ?? item.name ?? `#${item.id}`,
  }));
}
